/**
 * URL Filter Utilities
 * 
 * Parses URL query parameters and converts them to the AdvancedFilter format
 * used by the Leads page. Enables Insight dashboard action buttons to link
 * directly to a pre-filtered Leads list.
 */

// Map URL param keys to internal DB field paths and operators
const URL_PARAM_FILTER_MAP = {
  checklist_status: { field: 'checklist_status', op: 'eq' },
  approval_status: { field: 'commercial_approval.status', op: 'eq' },
  stage: { field: 'lead_stage', op: 'eq' },
  lead_stage: { field: 'lead_stage', op: 'eq' },
  lead_owner: { field: 'lead_owner', op: 'regex' },
  labels: { field: 'labels', op: 'in' },
  grb_property_id: { field: 'grb_property_id', op: 'regex' },
};

// Map user-friendly URL param values to actual DB field values
const VALUE_MAP = {
  approval_status: {
    pending: 'commercial_pending',
    l1_approved: 'leader_approved',
    l2_approved: 'bfr_approved',
    verified: 'verified',
  },
  stage: {
    executive_pending: 'bfr_approved',
  },
};

// When a URL value maps to a *different* field than the default, override here
const FIELD_OVERRIDE = {
  stage: {
    executive_pending: 'commercial_approval.status',
  },
};

/**
 * Convert URL search params into an AdvancedFilter object.
 * Returns an object compatible with the `activeFilters` format used by
 * AdvancedFilterPanel and the POST /leads/filter endpoint.
 */
export function applyFiltersFromURL(searchParams) {
  const filters = {};

  for (const [key, rawValue] of searchParams.entries()) {
    if (!rawValue) continue;

    const mapping = URL_PARAM_FILTER_MAP[key];
    if (!mapping) continue;

    // Labels are comma-separated IDs → use "in" operator with array value
    if (key === 'labels') {
      const ids = rawValue.split(',').map(s => s.trim()).filter(Boolean);
      if (ids.length > 0) {
        filters[mapping.field] = { op: 'in', value: ids };
      }
      continue;
    }

    const mappedValue = VALUE_MAP[key]?.[rawValue] || rawValue;
    const fieldOverride = FIELD_OVERRIDE[key]?.[rawValue];

    filters[fieldOverride || mapping.field] = {
      op: mapping.op,
      value: mappedValue,
    };
  }

  return filters;
}

/**
 * Check whether the current URL has any recognised filter params.
 */
export function hasURLFilters(searchParams) {
  for (const key of searchParams.keys()) {
    if (URL_PARAM_FILTER_MAP[key]) return true;
  }
  return false;
}

/**
 * Build a query string from a plain { key: value } object.
 */
export function buildFilterQueryString(params) {
  const sp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') sp.set(k, v);
  });
  return sp.toString();
}
